import React, { useState, useRef } from 'react';
import { useUploads } from '../../contexts/UploadsContext';
import './UploadsPopup.css';

const UploadsPopup = () => {
  const {
    uploads, isPopupOpen, closePopup, removeUpload, clearAllUploads,
    requestCancel, confirmCancel, dismissCancel, showCancelConfirm,
    expandUpload,
    resumeUpload, restartUpload, dismissInterrupted,
  } = useUploads();
  const [showClearWarning, setShowClearWarning] = useState(false);

  const pendingActionRef = useRef({ id: null, action: null });
  const fileInputRef = useRef(null);

  const handleResumeClick = (uploadId) => {
    pendingActionRef.current = { id: uploadId, action: 'resume' };
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleRestartClick = (uploadId) => {
    pendingActionRef.current = { id: uploadId, action: 'restart' };
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChosen = (e) => {
    const file = e.target.files?.[0];
    const { id, action } = pendingActionRef.current;
    pendingActionRef.current = { id: null, action: null };
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file || !id) return;
    if (action === 'resume') resumeUpload(id, file);
    else if (action === 'restart') restartUpload(id, file);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return 'fas fa-check-circle';
      case 'error':
        return 'fas fa-times-circle';
      case 'uploading':
        return 'fas fa-spinner fa-spin';
      case 'finalizing':
        return 'fas fa-spinner fa-spin';
      case 'skipped':
        return 'fas fa-forward';
      default:
        return 'fas fa-clock';
    }
  };

  const getFileStatusClass = (status) => {
    switch (status) {
      case 'success': return 'upload-item-status-success';
      case 'error': return 'upload-item-status-error';
      case 'uploading':
      case 'finalizing': return 'upload-item-status-active';
      case 'skipped': return 'upload-item-status-skipped';
      default: return 'upload-item-status-pending';
    }
  };

  const handleClearAll = () => {
    const activeUploads = uploads.filter(u => u.status === 'uploading');
    if (activeUploads.length > 0) {
      setShowClearWarning(true);
    } else {
      clearAllUploads();
    }
  };

  const handleConfirmClear = () => {
    setShowClearWarning(false);
    clearAllUploads();
  };

  const handleCancelClear = () => {
    setShowClearWarning(false);
  };

  if (!isPopupOpen) return null;

  return (
    <div className="uploads-popup">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChosen}
        style={{ display: 'none' }}
      />
      {/* Cancel confirmation overlay */}
      {showCancelConfirm && (
        <div className="uploads-cancel-confirm-overlay" onClick={dismissCancel}>
          <div className="uploads-cancel-confirm-popup" onClick={(e) => e.stopPropagation()}>
            <div className="uploads-cancel-confirm-header">
              <i className="fa-solid fa-triangle-exclamation uploads-cancel-confirm-warn-icon"></i>
              <span>Cancel Upload?</span>
            </div>
            <p className="uploads-cancel-confirm-copy">
              All files will be removed, including those already uploaded.
            </p>
            <div className="uploads-cancel-confirm-actions">
              <button className="uploads-cancel-confirm-yes" onClick={() => confirmCancel(showCancelConfirm)}>
                Yes, cancel
              </button>
              <button className="uploads-cancel-confirm-no" onClick={dismissCancel}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="uploads-popup-header">
        <div className="uploads-popup-header-left">
          <h3>Uploads</h3>
        </div>
        <button className="uploads-popup-close" onClick={closePopup} title="Close">
          <i className="fas fa-times"></i>
        </button>
      </div>
      {uploads.length > 0 && (
        <button className="uploads-popup-clear-all" onClick={handleClearAll} title="Clear All">
          Clear all
        </button>
      )}
      <div className="uploads-popup-content">
        {showClearWarning && (
          <div className="uploads-clear-warning-overlay">
            <div className="uploads-clear-warning-popup">
              <h4>This action will clear all upload entries!</h4>
              <div className="uploads-clear-warning-buttons">
                <button className="uploads-clear-warning-cancel" onClick={handleCancelClear}>
                  Go Back
                </button>
                <button className="uploads-clear-warning-confirm" onClick={handleConfirmClear}>
                  Approve
                </button>
              </div>
            </div>
          </div>
        )}
        {uploads.length === 0 ? (
          <div className="uploads-empty">
            <i className="fas fa-upload"></i>
            <p>No uploads</p>
          </div>
        ) : (
          <div className="uploads-list">
            {uploads.map((upload) => {
              const files = upload.files || [];
              const statuses = upload.fileStatuses || {};
              const progress = upload.fileProgress || {};
              const successCount = files.filter((_, i) => statuses[i] === 'success').length;
              const errorCount = files.filter((_, i) => statuses[i] === 'error').length;
              const isActive = upload.status === 'uploading' || upload.status === 'finalizing';
              const overallPct = files.length > 0
                ? Math.round(Object.values(progress).reduce((a, b) => a + b, 0) / files.length)
                : 0;

              return (
                <div key={upload.id} className="upload-item">
                  <div className="upload-item-info">
                    <div className="upload-item-icon">
                      {upload.status === 'uploading' || upload.status === 'finalizing' ? (
                        <div className="upload-loading-circle"></div>
                      ) : upload.status === 'interrupted' || upload.status === 'mismatch' ? (
                        <i className="fas fa-pause-circle"></i>
                      ) : upload.status === 'failed' || upload.status === 'error' ? (
                        <i className="fas fa-exclamation-circle"></i>
                      ) : upload.status === 'locked' ? (
                        <i className="fas fa-lock"></i>
                      ) : (
                        <i className="fas fa-check-circle"></i>
                      )}
                    </div>
                    <div className="upload-item-details">
                      <div className="upload-item-name">{upload.folderName}</div>
                      <div className="upload-item-status">
                        {upload.status === 'uploading' && `Uploading... ${overallPct}% (${successCount}/${files.length})`}
                        {upload.status === 'interrupted' && `Interrupted at ${overallPct}% — pick file to resume`}
                        {upload.status === 'mismatch' && `File doesn't match — pick the same file`}
                        {upload.status === 'failed' && `Upload expired — please restart`}
                        {upload.status === 'locked' && `Resumed in another tab`}
                        {upload.status === 'completed' && `${successCount} file${successCount !== 1 ? 's' : ''} uploaded`}
                        {upload.status === 'error' && `${successCount} uploaded, ${errorCount} failed`}
                      </div>
                    </div>
                  </div>
                  <div className="upload-item-actions">
                    {upload.status === 'uploading' && (
                      <>
                        <button className="upload-item-expand-btn" onClick={() => expandUpload(upload.id)} title="Open full upload modal">
                          <i className="fas fa-expand-alt"></i>
                        </button>
                        <button className="upload-item-cancel-btn" onClick={() => requestCancel(upload.id)} title="Cancel upload">
                          <i className="fas fa-stop"></i>
                        </button>
                      </>
                    )}
                    {upload.status === 'interrupted' && (
                      <>
                        <button className="upload-item-resume-btn" onClick={() => handleResumeClick(upload.id)} title="Resume (re-pick file)">
                          <i className="fas fa-play"></i>
                        </button>
                        <button className="upload-item-restart-btn" onClick={() => handleRestartClick(upload.id)} title="Restart from scratch">
                          <i className="fas fa-redo"></i>
                        </button>
                        <button className="upload-item-remove-btn" onClick={() => dismissInterrupted(upload.id)} title="Dismiss">
                          <i className="fas fa-times"></i>
                        </button>
                      </>
                    )}
                    {upload.status === 'mismatch' && (
                      <>
                        <button className="upload-item-resume-btn" onClick={() => handleResumeClick(upload.id)} title="Try a different file">
                          <i className="fas fa-folder-open"></i>
                        </button>
                        <button className="upload-item-remove-btn" onClick={() => dismissInterrupted(upload.id)} title="Dismiss">
                          <i className="fas fa-times"></i>
                        </button>
                      </>
                    )}
                    {upload.status === 'failed' && (
                      <>
                        <button className="upload-item-restart-btn" onClick={() => handleRestartClick(upload.id)} title="Restart from scratch">
                          <i className="fas fa-redo"></i>
                        </button>
                        <button className="upload-item-remove-btn" onClick={() => dismissInterrupted(upload.id)} title="Dismiss">
                          <i className="fas fa-times"></i>
                        </button>
                      </>
                    )}
                    {upload.status === 'locked' && (
                      <>
                        <span className="upload-item-locked-note">Resumed in another tab</span>
                        <button className="upload-item-remove-btn" onClick={() => dismissInterrupted(upload.id)} title="Dismiss">
                          <i className="fas fa-times"></i>
                        </button>
                      </>
                    )}
                    {(upload.status === 'completed' || upload.status === 'error') && (
                      <button className="upload-item-remove-btn" onClick={() => removeUpload(upload.id)} title="Remove">
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                  {isActive && (
                    <div className="upload-item-progress-bar">
                      <div className="upload-item-progress-fill" style={{ width: `${overallPct}%` }}></div>
                    </div>
                  )}
                  {files.length > 0 && (
                    <div className="upload-item-files">
                      {files.map((file, idx) => (
                        <div key={`${file.name}-${idx}`} className={`upload-item-file ${getFileStatusClass(statuses[idx])}`}>
                          <i className={getStatusIcon(statuses[idx] || 'pending')}></i>
                          <span className="upload-item-file-name" title={file.name}>{file.name}</span>
                          {(statuses[idx] === 'uploading' || statuses[idx] === 'finalizing') && (
                            <span className="upload-item-file-pct">{progress[idx] || 0}%</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadsPopup;
